import { z } from 'zod';
import { ApprovalSchema } from './admin.js';
import { PublicationSchema, SectionKeySchema, SubjectDraftSchema } from './catalog.js';
import { IdSchema, PublicationIdSchema, TenantIdSchema } from './common.js';

/*
 * Clientes, apps e contexto versionado por app (camada universal, fase 1).
 *
 * Cadeia: cliente -> app (ativo de uma fonte) -> versões do contexto do app.
 * Cada app tem no máximo uma versão publicada e um rascunho aberto. Publicar uma versão recompõe
 * a publicação do tenant com a versão publicada de cada app: é ela que as ferramentas leem, então
 * resultado, auditoria e explain_result continuam apontando para uma publicação.
 */

export const UuidSchema = z.string().uuid();

// ---------- Clientes ----------

export const ClientSchema = z.object({
  id: UuidSchema,
  tenant_id: TenantIdSchema,
  name: z.string().trim().min(1).max(120),
  /** O que o agente precisa saber sobre a empresa em todos os apps dela: o que faz, siglas, público, regras gerais. */
  context_md: z.string().max(20_000).default(''),
  /** Valores fora da IA: ela vê nomes de colunas e quantidades; os números ficam na tela. Ligado por padrão. */
  privacy_mode: z.boolean().default(true),
  /** Perguntas restantes no chat próprio. Nulo: sem limite. */
  credits_left: z.number().int().min(0).nullable().default(null),
  /** Marca e voz do chat do cliente (fase 5). Guardados como chegam, validados pela tela que os usa. */
  branding: z.record(z.string(), z.unknown()).default({}),
  voice: z.record(z.string(), z.unknown()).default({}),
  created_by: z.string().min(1),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Client = z.infer<typeof ClientSchema>;

export const ClientSummarySchema = ClientSchema.extend({
  apps_total: z.number().int(),
  apps_published: z.number().int(),
});
export type ClientSummary = z.infer<typeof ClientSummarySchema>;

export const ClientCreateSchema = z.object({
  name: ClientSchema.shape.name,
  context_md: ClientSchema.shape.context_md.optional(),
});
export type ClientCreate = z.infer<typeof ClientCreateSchema>;

export const ClientUpdateSchema = z
  .object({
    name: ClientSchema.shape.name,
    context_md: z.string().max(20_000),
    privacy_mode: z.boolean(),
    credits_left: z.number().int().min(0).nullable(),
  })
  .partial();
export type ClientUpdate = z.infer<typeof ClientUpdateSchema>;

// ---------- Apps (ativos das fontes) ----------

/** Tipo do ativo na fonte. Hoje só app do Qlik; o campo existe para outras fontes entrarem sem mudar o modelo. */
export const AssetKindSchema = z.enum(['qlik_app']);
export type AssetKind = z.infer<typeof AssetKindSchema>;

/** nao_habilitado: só listado. rascunho: tem contexto em edição, ainda não responde. publicado: os agentes usam. */
export const AssetStatusSchema = z.enum(['nao_habilitado', 'rascunho', 'publicado']);
export type AssetStatus = z.infer<typeof AssetStatusSchema>;

export const AssetSchema = z.object({
  id: UuidSchema,
  tenant_id: TenantIdSchema,
  /** Id da conexão em sources (lastrouter.yaml ou registro de tenants). */
  source_id: IdSchema,
  client_id: UuidSchema.nullable(),
  kind: AssetKindSchema,
  /** Id do ativo na fonte: no Qlik, o GUID do app. */
  external_id: z.string().min(1).max(256),
  name: z.string().min(1).max(500),
  /** Stream (on-prem) ou espaço (Cloud). */
  space: z.string().max(500).nullable(),
  owner: z.string().max(500).nullable(),
  last_reload_at: z.string().nullable(),
  /** Última vez que a sincronização encontrou o ativo na fonte. */
  seen_at: z.string(),
  /** A última sincronização não encontrou o ativo (apagado ou sem acesso para quem sincronizou). */
  missing: z.boolean(),
  status: AssetStatusSchema,
  /** Id do assunto deste app no catálogo publicado. Único no tenant. */
  subject_id: IdSchema,
  active_version_id: UuidSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Asset = z.infer<typeof AssetSchema>;

/** O que um adaptador de fonte devolve ao listar os ativos que o usuário enxerga. */
export const DiscoveredAssetSchema = z.object({
  kind: AssetKindSchema,
  external_id: z.string().min(1).max(256),
  name: z.string().min(1).max(500),
  space: z.string().max(500).nullable(),
  owner: z.string().max(500).nullable(),
  last_reload_at: z.string().nullable(),
  description: z.string().max(10_000).nullable(),
});
export type DiscoveredAsset = z.infer<typeof DiscoveredAssetSchema>;

export const AssetSyncResultSchema = z.object({
  source_id: IdSchema,
  /** Usuário da fonte que listou: a lista é a que ele enxerga. */
  listed_as: z.string(),
  added: z.number().int(),
  updated: z.number().int(),
  missing: z.number().int(),
});
export type AssetSyncResult = z.infer<typeof AssetSyncResultSchema>;

export const AssetUpdateSchema = z
  .object({
    client_id: UuidSchema.nullable(),
    subject_id: IdSchema,
    /** Só para desligar: um app publicado volta a nao_habilitado e sai do catálogo. Publicar é pela versão. */
    status: z.literal('nao_habilitado'),
  })
  .partial();
export type AssetUpdate = z.infer<typeof AssetUpdateSchema>;

// ---------- Versões do contexto ----------

/** rascunho: em edição (um por app). publicada: a que vale (uma por app). arquivada: já valeu. descartada: rascunho abandonado. */
export const VersionStateSchema = z.enum(['rascunho', 'publicada', 'arquivada', 'descartada']);
export type VersionState = z.infer<typeof VersionStateSchema>;

/** Publicação feita pelo admin sem a validação do cliente (ADR 0050): quem, quando, por quê e o que faltava. */
export const UnvalidatedPublishSchema = z.object({
  by: z.string().min(1),
  at: z.string(),
  justification: z.string().min(10).max(2000),
  /** O que estava pendente na hora: aprovações dos donos e condições da certificação que falhavam. */
  pending: z.array(z.string()),
});
export type UnvalidatedPublish = z.infer<typeof UnvalidatedPublishSchema>;

export const ContextVersionSchema = z.object({
  id: UuidSchema,
  tenant_id: TenantIdSchema,
  asset_id: UuidSchema,
  /** 1, 2, 3... por app, na ordem de criação. */
  number: z.number().int().min(1),
  state: VersionStateSchema,
  /** O contexto inteiro do app: camada semântica (dimensões, métricas, consultas, roteiros) e as seções de texto. */
  subject: SubjectDraftSchema,
  content_hash: z.string(),
  /** Versão publicada de onde este rascunho nasceu; nulo no primeiro. */
  parent_id: UuidSchema.nullable(),
  reason: z.string().max(500),
  /** O que mudou em relação à versão de origem, uma linha por item. */
  summary: z.array(z.string()),
  approvals: z.array(ApprovalSchema).default([]),
  created_by: z.string().min(1),
  created_at: z.string(),
  updated_at: z.string(),
  published_by: z.string().nullable(),
  published_at: z.string().nullable(),
  /** Publicação do tenant criada quando esta versão entrou no ar. */
  publication_id: PublicationIdSchema.nullable(),
  /** Preenchido quando o admin publicou sem a validação do cliente; nulo no fluxo normal. */
  unvalidated_publish: UnvalidatedPublishSchema.nullable().default(null),
});
export type ContextVersion = z.infer<typeof ContextVersionSchema>;

export const VersionApprovalStatusSchema = z.object({
  /** Todas as aprovações exigidas e nenhuma recusa: o admin pode publicar. */
  ready: z.boolean(),
  refused: z.boolean(),
  pending: z.array(z.object({ papel: z.enum(['negocio', 'tecnico']), owner: z.string() })),
});
export type VersionApprovalStatus = z.infer<typeof VersionApprovalStatusSchema>;

export const VersionDetailSchema = z.object({
  version: ContextVersionSchema,
  status: VersionApprovalStatusSchema,
  /** Problemas que impedem publicar, uma linha por campo (formatIssues do SubjectSchema). Vazio: pronto. */
  issues: z.array(z.string()),
  /** Papéis que a pessoa logada pode aprovar nesta versão. */
  can_approve: z.array(z.enum(['negocio', 'tecnico'])),
  /** Papéis pendentes que o admin pode decidir no lugar do dono ausente (com justificativa). */
  can_substitute: z.array(z.enum(['negocio', 'tecnico'])).default([]),
});
export type VersionDetail = z.infer<typeof VersionDetailSchema>;

export const AssetDetailSchema = z.object({
  asset: AssetSchema,
  client: ClientSchema.nullable(),
  published: ContextVersionSchema.nullable(),
  draft: VersionDetailSchema.nullable(),
});
export type AssetDetail = z.infer<typeof AssetDetailSchema>;

export const AssetSummarySchema = AssetSchema.extend({
  client_name: z.string().nullable(),
  published_number: z.number().int().nullable(),
  draft_number: z.number().int().nullable(),
  /** A versão publicada foi ao ar sem a validação do cliente (ADR 0050). */
  published_unvalidated: z.boolean().default(false),
});
export type AssetSummary = z.infer<typeof AssetSummarySchema>;

/** Situação na busca de apps: as três do app e "sumiu da fonte" (missing). Valores da URL e da API, em inglês. */
export const AssetStatusFilterSchema = z.enum(['published', 'draft', 'not_enabled', 'missing']);
export type AssetStatusFilter = z.infer<typeof AssetStatusFilterSchema>;

/** Ordem da lista; com "-" na frente, ao contrário. last_reload: última recarga dos dados na fonte. */
export const AssetSortSchema = z.enum(['name', '-name', 'status', '-status', 'client', '-client', 'last_reload', '-last_reload']);
export type AssetSort = z.infer<typeof AssetSortSchema>;

export const AssetSearchSchema = z.object({
  /** Procura no nome, no id do app na fonte, no espaço, no id do assunto e no nome do cliente. */
  q: z.string().trim().max(200).optional(),
  status: AssetStatusFilterSchema.optional(),
  /** Uuid do cliente, ou 'none' para os apps sem cliente. */
  client_id: z.union([UuidSchema, z.literal('none')]).optional(),
  source_id: IdSchema.optional(),
  sort: AssetSortSchema.default('name'),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(100).default(25),
});
export type AssetSearch = z.input<typeof AssetSearchSchema>;

/** Contagem por situação com a busca e os outros filtros (menos o de situação). all: todos. */
export const AssetCountsSchema = z.object({
  all: z.number().int(),
  published: z.number().int(),
  draft: z.number().int(),
  not_enabled: z.number().int(),
  missing: z.number().int(),
});
export type AssetCounts = z.infer<typeof AssetCountsSchema>;

export const AssetPageSchema = z.object({
  items: z.array(AssetSummarySchema),
  /** Quantos apps passam em todos os filtros (todas as páginas). */
  total: z.number().int(),
  page: z.number().int(),
  page_size: z.number().int(),
  counts: AssetCountsSchema,
  /** A fonte não respondeu com a lista de apps da pessoa: a página vem vazia. */
  access_unavailable: z.boolean().optional(),
});
export type AssetPage = z.infer<typeof AssetPageSchema>;

/** Mesma ação em vários apps de uma vez: as que já existem uma a uma. */
export const AssetBulkSchema = z.object({
  /** set_client: liga ao cliente (client_id nulo tira do cliente). enable: abre o contexto vazio. disable: desliga. */
  action: z.enum(['set_client', 'enable', 'disable']),
  ids: z.array(UuidSchema).min(1).max(100),
  client_id: UuidSchema.nullable().optional(),
});
export type AssetBulk = z.infer<typeof AssetBulkSchema>;

export const AssetBulkResultSchema = z.object({
  changed: z.number().int(),
  /** Já estavam como pedido (habilitar um app já habilitado, por exemplo). */
  skipped: z.number().int(),
  failed: z.array(z.object({ id: z.string(), name: z.string().nullable(), message: z.string() })),
});
export type AssetBulkResult = z.infer<typeof AssetBulkResultSchema>;

// ---------- Histórico de alterações do contexto ----------

/** Onde a alteração foi feita. */
export const ChangeSourceSchema = z.enum(['painel', 'agente', 'chat', 'importacao', 'geracao', 'reversao']);
export type ChangeSource = z.infer<typeof ChangeSourceSchema>;

/** Parte do contexto alterada: uma das seções de texto ou a camada semântica inteira. */
export const ChangeTargetSchema = z.union([SectionKeySchema, z.literal('semantica')]);
export type ChangeTarget = z.infer<typeof ChangeTargetSchema>;

export const ContextChangeSchema = z.object({
  id: UuidSchema,
  tenant_id: TenantIdSchema,
  asset_id: UuidSchema,
  version_id: UuidSchema,
  target: ChangeTargetSchema,
  operation: z.enum(['add', 'replace', 'remove']),
  /** Texto da seção antes e depois. Na camada semântica, o JSON canônico. */
  before_text: z.string(),
  after_text: z.string(),
  reason: z.string().max(500).nullable(),
  user: z.string().min(1),
  source: ChangeSourceSchema,
  created_at: z.string(),
});
export type ContextChange = z.infer<typeof ContextChangeSchema>;

// ---------- Rotas do painel (entram em ADMIN_ROUTES) ----------

const UuidParam = z.object({ id: UuidSchema });

export const APPS_ROUTES = {
  clients: { method: 'GET', path: '/clients', access: 'admin', summary: 'Clientes com a contagem de apps', response: z.array(ClientSummarySchema) },
  createClient: { method: 'POST', path: '/clients', access: 'admin', summary: 'Cria um cliente', body: ClientCreateSchema, response: ClientSchema },
  client: { method: 'GET', path: '/clients/:id', access: 'admin', summary: 'Um cliente', params: UuidParam, response: ClientSchema },
  updateClient: { method: 'PATCH', path: '/clients/:id', access: 'admin', summary: 'Altera um cliente', params: UuidParam, body: ClientUpdateSchema, response: ClientSchema },
  deleteClient: {
    method: 'POST',
    path: '/clients/:id/delete',
    access: 'admin',
    summary: 'Exclui um cliente; os apps dele ficam sem cliente, com o contexto intacto',
    params: UuidParam,
    response: z.object({ ok: z.literal(true) }),
  },
  assets: {
    method: 'GET',
    path: '/assets',
    access: 'owner',
    summary: 'Apps das fontes, com estado e cliente',
    query: z.object({ client_id: z.union([UuidSchema, z.literal('none')]).optional(), source_id: IdSchema.optional() }),
    response: z.array(AssetSummarySchema),
  },
  searchAssets: {
    method: 'GET',
    path: '/assets/search',
    access: 'owner',
    summary: 'Apps com busca, filtros, ordem e página, mais a contagem por situação',
    query: AssetSearchSchema,
    response: AssetPageSchema,
  },
  bulkAssets: {
    method: 'POST',
    path: '/assets/bulk',
    access: 'admin',
    summary: 'Mesma ação em vários apps: ligar a um cliente, habilitar ou desligar',
    body: AssetBulkSchema,
    response: AssetBulkResultSchema,
  },
  syncAssets: {
    method: 'POST',
    path: '/assets/sync',
    access: 'admin',
    summary: 'Lista os apps da fonte como o admin logado e atualiza o registro',
    body: z.object({ source_id: IdSchema }),
    response: AssetSyncResultSchema,
  },
  asset: { method: 'GET', path: '/assets/:id', access: 'owner', summary: 'Um app com a versão publicada e o rascunho', params: UuidParam, response: AssetDetailSchema },
  updateAsset: { method: 'PATCH', path: '/assets/:id', access: 'admin', summary: 'Liga o app a um cliente, troca o id do assunto ou desliga', params: UuidParam, body: AssetUpdateSchema, response: AssetSchema },
  assetVersions: { method: 'GET', path: '/assets/:id/versions', access: 'owner', summary: 'Histórico de versões do contexto do app', params: UuidParam, response: z.array(ContextVersionSchema) },
  assetChanges: { method: 'GET', path: '/assets/:id/changes', access: 'owner', summary: 'Alterações do contexto do app, da mais nova para a mais antiga', params: UuidParam, response: z.array(ContextChangeSchema) },
  createVersion: {
    method: 'POST',
    path: '/assets/:id/versions',
    access: 'owner',
    summary: 'Abre um rascunho: a partir da versão publicada, de uma versão antiga ou vazio',
    params: UuidParam,
    body: z.object({ from_version_id: UuidSchema.nullable().default(null), reason: z.string().min(1).max(500) }),
    response: VersionDetailSchema,
  },
  version: { method: 'GET', path: '/versions/:id', access: 'owner', summary: 'Uma versão com aprovações e pendências', params: UuidParam, response: VersionDetailSchema },
  saveVersion: {
    method: 'PUT',
    path: '/versions/:id',
    access: 'owner',
    summary: 'Salva o rascunho (zera as aprovações e registra o que mudou)',
    params: UuidParam,
    body: z.object({ subject: z.unknown(), reason: z.string().min(1).max(500), expected_updated_at: z.string() }),
    response: VersionDetailSchema,
  },
  approveVersion: {
    method: 'POST',
    path: '/versions/:id/approve',
    access: 'owner',
    summary: 'Aprova ou recusa o rascunho como dono de negócio ou técnico',
    params: UuidParam,
    body: z.object({
      papel: z.enum(['negocio', 'tecnico']),
      decision: z.enum(['aprovado', 'recusado']),
      comment: z.string().max(2000).nullable().default(null),
      /** Admin decide no lugar do dono ausente; exige comment como justificativa. */
      no_lugar_do_dono: z.boolean().default(false),
      /** updated_at do rascunho que o dono viu: se mudou, a decisão é recusada (409 stale). Sem ele, vale o lido pelo serviço. */
      expected_updated_at: z.string().optional(),
    }),
    response: VersionDetailSchema,
  },
  publishVersion: {
    method: 'POST',
    path: '/versions/:id/publish',
    access: 'admin',
    summary: 'Publica o rascunho aprovado: o app passa a responder com esta versão',
    params: UuidParam,
    response: z.object({ version: ContextVersionSchema, publication: PublicationSchema }),
  },
  publishVersionUnvalidated: {
    method: 'POST',
    path: '/versions/:id/publish-unvalidated',
    access: 'admin',
    summary: 'Publica o rascunho sem a validação do cliente: decide no lugar dos donos ausentes, com justificativa, alerta e auditoria',
    params: UuidParam,
    body: z.object({ justification: z.string().max(2000) }),
    response: z.object({ version: ContextVersionSchema, publication: PublicationSchema }),
  },
  discardVersion: { method: 'POST', path: '/versions/:id/discard', access: 'owner', summary: 'Descarta o rascunho', params: UuidParam, response: ContextVersionSchema },
} as const;
